import MuiInputLabel from '@mui/material/InputLabel';
import MuiFormControl from '@mui/material/FormControl';
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import { equals } from 'ramda';
import { ListItemText } from '@mui/material';
import EDTypography from '../../Typography/EDTypography';
import EDTooltip from '../../Tooltip/EDTooltip';
import { getSelectChoiceNameFromValue, ISelectChoice } from '../SelectCommons';
import EDMenuItem from '../../Menu/EDMenuItem';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import { makeStyles } from 'tss-react/mui';
import EDListItemIcon from '../../List/Components/EDListItemIcon';
import EDIcon from '../../Icon/EDIcon';
import useInputVariant from '../../../Hooks/useInputVariant';

interface IProps<T> {
  value?: T;
  renderValue?: (value: T) => React.ReactNode;
  onChange: (newValue: T) => void;
  items: Array<ISelectChoice<T>>;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
  testId?: string;
  margin?: CommonPropsAndTypes.StandardSpacingType;
  error?: boolean;
  fullWidth?: boolean;
  shrink?: boolean;
  // TODO: Replace with classes useage when material ui 4 is introduced with styles hook
  formControlRoot?: string;
  rootClass?: string;
  selectMenuClass?: string;
  inputClass?: string;
  underlineClass?: string;
  labelClass?: string;
  labelFocusedClass?: string;
  iconClass?: string;
  simpleInput?: boolean;
  gridContainer?: boolean;
  sections?: string[];
  tooltip?: string;
}

const useStyles = makeStyles()(theme => ({
  filled: {
    transform: 'translate(4px, 2.5px) scale(0.75)', // FIXME: borde vara i temat.
  },
  outlined: {
    '&:focus': {
      backgroundColor: theme.palette.common.white,
    },
  },
  gridContainer: {
    marginTop: theme.spacing(2) + 0.6, // FIXME: varför så udda värde?
    width: `calc(100% - ${theme.spacing(2)})`,
  },
}));

function renderMenuItem<T>(
  item: ISelectChoice<T>,
  value: T
): React.ReactElement<any> {
  return (
    <EDMenuItem
      disabled={item.disabled}
      disabledReason={item.disabledReason}
      key={item.header}
      value={item.value}
      data-testid={item.testId ? item.testId : undefined}
    >
      {item.icon && (
        <EDListItemIcon>
          <EDIcon color="action" iconType={item.icon} />
        </EDListItemIcon>
      )}
      <ListItemText
        primary={
          <EDTypography color="textPrimary" bold={equals(item.value, value)}>
            {item.listHeader ? item.listHeader : item.header}
          </EDTypography>
        }
        secondary={
          <EDTypography color="textPrimary" component="span">
            {item.subHeader}
          </EDTypography>
        }
      />
    </EDMenuItem>
  );
}

// Try to make sure the value we send to material-ui is one of our items
function getValue<T>(value: T, items: Array<ISelectChoice<T>>): T {
  let result = value;
  items.forEach(itemVal => {
    if (equals(itemVal.value, value)) {
      result = itemVal.value;
    }
  });
  return result;
}

/**
 * Komponent som visar ett select field, med standardiserat utseende
 */
const EDStandardSelect = <T extends string | boolean | number | object>(
  props: IProps<T>
) => {
  const { classes, cx } = useStyles(undefined, {
    props: props as any,
  });
  const inputVariant = useInputVariant();
  // const [labelWidth, setLabelWidth] = React.useState(0);
  // const labelRef = React.useRef<HTMLLabelElement | null>(null);
  // React.useEffect(() => {
  //   if (inputVariant === 'outlined') {
  //     setLabelWidth(
  //       labelRef.current != null ? labelRef.current.offsetWidth : 0
  //     );
  //   }
  // }, [
  //   inputVariant,
  //   props.label,
  //   props.shrink,
  //   props.simpleInput,
  //   props.labelClass,
  //   props.labelFocusedClass,
  // ]);

  const onChange = (event: SelectChangeEvent<T>, child: React.ReactNode) => {
    const val = props.items.find(v => equals(v.value, event.target.value as T));
    if (val) {
      props.onChange(val.value);
    }
  };

  const { sections } = props;

  const sectionItems: { [key: string]: Array<ISelectChoice<T>> } = {};
  if (sections) {
    sections.forEach(section => {
      sectionItems[section] = props.items.filter(item =>
        equals(item.section, section)
      );
    });
  }

  return (
    <EDTooltip
      title={
        props.disabled
          ? props.disabledReason
          : props.tooltip
          ? props.tooltip
          : undefined
      }
      enableClickOnTouch={props.disabled}
    >
      <MuiFormControl
        margin={props.margin ? props.margin : 'normal'}
        error={props.error}
        fullWidth={props.fullWidth != null ? props.fullWidth : true}
        classes={{
          root: props.gridContainer
            ? cx(props.formControlRoot, classes.gridContainer)
            : props.formControlRoot,
        }}
        size="small"
      >
        {props.label && (
          <MuiInputLabel
            id={`${props.label}-label`}
            htmlFor={props.label}
            shrink={props.shrink}
            classes={{
              root: props.simpleInput
                ? props.labelClass
                : cx(props.labelClass, {
                    [classes.filled]: inputVariant !== 'outlined',
                  }),
              focused: props.labelFocusedClass,
            }}
            // ref={labelRef}
            variant={inputVariant}
          >
            {props.label}
          </MuiInputLabel>
        )}
        <MuiSelect<T>
          variant={inputVariant}
          value={getValue(props.value, props.items)}
          onChange={onChange}
          label={props.label}
          labelId={`${props.testId ?? props.label}-label`}
          // input={
          //   props.simpleInput ? (
          //     <MuiInput
          //       id={props.label}
          //       classes={{
          //         root: props.inputClass,
          //         underline: props.underlineClass,
          //       }}
          //     />
          //   ) : inputVariant === 'outlined' ? (
          //     <MuiOutlinedInput
          //       id={props.label}
          //       classes={{
          //         root: props.inputClass,
          //       }}
          //       margin="dense"
          //     />
          //   ) : (
          //     <MuiFilledInput
          //       id={props.label}
          //       classes={{
          //         root: props.inputClass,
          //         underline: props.underlineClass,
          //       }}
          //       margin="dense"
          //     />
          //   )
          // }
          classes={{
            // selectMenu: props.selectMenuClass,
            // root: props.rootClass,
            icon: props.iconClass,
            outlined: classes.outlined,
          }}
          renderValue={(value: T) =>
            props.renderValue
              ? props.renderValue(value)
              : getSelectChoiceNameFromValue(value, props.items)
          }
          SelectDisplayProps={
            { 'data-testid': props.testId } as React.HTMLAttributes<HTMLElement>
          }
          disabled={props.disabled}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            // getContentAnchorEl: null,
          }}
        >
          {(!sections || sections.length === 0) &&
            props.items.map(item => {
              return renderMenuItem(item, props.value);
            })}
          {sections &&
            sections.length > 0 && [
              [
                ...props.items
                  .filter(item => !sections.includes(item.section || ''))
                  .map(item => renderMenuItem(item, props.value)),
              ],
              ...sections.map((section, i) => {
                return sectionItems[section] && sectionItems[section].length > 0
                  ? [
                      <EDMenuItem key={i}>
                        <EDTypography color="primary" variant="body2">
                          {section}
                        </EDTypography>
                      </EDMenuItem>,
                      [
                        ...sectionItems[section].map(item =>
                          renderMenuItem(item, props.value)
                        ),
                      ],
                    ]
                  : null;
              }),
            ]}
        </MuiSelect>
      </MuiFormControl>
    </EDTooltip>
  );
};

export default EDStandardSelect;
